import type { Node } from '@atlaskit/editor-prosemirror/model';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility/types';

import { expVal } from '@confluence/feature-experiments';

import { DRAW_IO_HEIGHT_OFFSET, getDrawioHeight, isDrawioExtensionKey } from './getDrawioHeight.ts';
/**
 * Separating Connect/Legacy extension height reservation from useExtensionNodeParams. Forge
 * related improvements already exist and will be formalized to achieve complete CLS reduction
 * for UI kit and Custom UI.
 */

/**
 * Currently, very few legacy (Connect) macros provide their expected rendered height/width
 * values in their macro parameters. Of those that do, there's a slight but consistent
 * margin of error for the rendered height value. Others that don't render dynamic content we can
 * set a default height for (such as Miro embed).
 *
 * Any changes here should be mirrored in content-renderer-legacy-macros/src/useConnectExtensionViewportSize.ts
 * and ./useExtensionNodeParams.ts
 */
const GOOGLE_DRIVE_EMBED_FILE_OFFSET_HEIGHT = 79;
const PLANTUML_OFFSET_HEIGHT = 40;

// Default sizes
const MIRO_EMBED_DEFAULT_HEIGHT = 570;
const EASY_MATH_BLOCK_DEFAULT_HEIGHT = 47;
const MATH_BLOCK_DEFAULT_HEIGHT = 58;
const PAGE_SIGNATURES_DEFAULT_HEIGHT = 23;
const QUESTIONS_LIST_DEFAULT_HEIGHT = 800;
const JIRA_ROADMAP_DEFAULT_HEIGHT = 600;
const LUCID_CHART_DEFAULT_HEIGHT = 500;
const GLIFFY_DEFAULT_HEIGHT = 150;
const AURA_DIVIDER_DEFAULT_HEIGHT = 21;
const AURA_CARDS_DEFAULT_HEIGHT = 226;

const getHeightFromNode = (node: Node) => {
	if (!node) return;

	const extensionKey = node.attrs.extensionKey;
	const params = node.attrs.parameters?.macroParams;

	// Supports Forge draw.io (incl. diagrams migrated from Connect) via guestParams; see getDrawioHeight.ts
	if (
		isDrawioExtensionKey(extensionKey) &&
		expVal('confluence_preset_height_migrated_drawio', 'isEnabled', false)
	) {
		return getDrawioHeight(node.attrs.parameters);
	}

	if (
		extensionKey === 'drawio' ||
		extensionKey === 'inc-drawio' ||
		extensionKey === 'drawio-sketch'
	) {
		/**
		 * Zoom can also change the desired dimensions of the diagram meaning we'll have to exclude diagrams with a zoom
		 * parameter not equal to 1
		 */
		const diagramWidth = Number(params?.width?.value);
		const zoomFactor = Number(params?.zoom?.value);
		/**
		 * Extension nodes in the editor can range between 556px and 760px wide depending on the browser width.
		 * For smaller viewport sizes (i.e. 500px), Draw.io will resize the height of the content based
		 * on its ratio for widths above 530px. To simplify the exclusion of diagrams that Draw.io would resize,
		 * we will exclude any diagrams that could be resized by Draw.io.
		 */
		if ((diagramWidth && diagramWidth > 530) || zoomFactor !== 1) {
			// Exclude wide diagrams due to allow resizing by vendor
			return;
		}
	}

	if (extensionKey === 'plantumlcloud') {
		return params?.originalHeight?.value;
	}

	return params?.height?.value;
};

export const getCalculatedHeightFromNode: GetPMNodeHeight = (node: Node) => {
	const extensionKey = node.attrs.extensionKey;
	const nodeHeight = getHeightFromNode(node);

	let height: number | undefined = Number(nodeHeight);

	if (!Number.isNaN(height)) {
		// for extensions that provide a number, add their offset
		switch (extensionKey) {
			case 'drawio':
			case 'inc-drawio':
			case 'drawio-sketch':
				height += DRAW_IO_HEIGHT_OFFSET;
				break;
			case 'lref-gdrive-embedded-file':
				height += GOOGLE_DRIVE_EMBED_FILE_OFFSET_HEIGHT;
				break;
			case 'plantumlcloud':
				height += PLANTUML_OFFSET_HEIGHT;
				break;
			default:
				// Forge draw.io keys (`<appId>/<envId>/static/drawio`)
				if (
					isDrawioExtensionKey(extensionKey) &&
					expVal('confluence_preset_height_migrated_drawio', 'isEnabled', false)
				) {
					height += DRAW_IO_HEIGHT_OFFSET;
				}
				break;
		}
	} else {
		// for extensions that don't resize based on their content, we can set a default height
		switch (extensionKey) {
			case 'gliffy':
				height = GLIFFY_DEFAULT_HEIGHT;
				break;
			case 'aura-divider':
				height = AURA_DIVIDER_DEFAULT_HEIGHT;
				break;
			case 'aura-cards':
				height = AURA_CARDS_DEFAULT_HEIGHT;
				break;
			case 'miro-macro-resizing':
				height = MIRO_EMBED_DEFAULT_HEIGHT;
				break;
			case 'easy-math-block':
				height = EASY_MATH_BLOCK_DEFAULT_HEIGHT;
				break;
			case 'mathblock':
				height = MATH_BLOCK_DEFAULT_HEIGHT;
				break;
			case 'page-signatures':
				height = PAGE_SIGNATURES_DEFAULT_HEIGHT;
				break;
			case 'questionslist-macro':
				height = QUESTIONS_LIST_DEFAULT_HEIGHT;
				break;
			case 'jiraroadmap':
				height = JIRA_ROADMAP_DEFAULT_HEIGHT;
				break;
			case 'lucidchart':
				height = LUCID_CHART_DEFAULT_HEIGHT;
				break;
			default:
				height = undefined;
				break;
		}
	}

	return height?.toString();
};
